import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { AuthContextService } from './auth-context.service';

type SessionInvalidationPayload = {
  id_token: string;
};

@Injectable({
  providedIn: 'root'
})
export class SessionInvalidationService {
  constructor(
    private http: HttpClient,
    private authContextService: AuthContextService,
    @Inject('API_URL') private apiUrl: string
  ) {}

  invalidateSession(): Observable<boolean> {
    const payload = this.buildPayload();
    const headers = this.buildHeaders();

    return this.http.post<void>(`${this.apiUrl}/user/session/invalidate`, payload, { headers }).pipe(
      timeout(5000),
      map(() => true),
      catchError((error) => {
        console.error('Failed to invalidate user session:', error);
        return of(false);
      })
    );
  }

  /**
   * Fire-and-forget invalidate call for tab/browser close: fetch+keepalive survives page
   * unload, unlike HttpClient (XHR, no keepalive) which also goes through the auth
   * interceptor's token-refresh check first.
   */
  invalidateSessionOnUnload(): void {
    try {
      const payload = this.buildPayload();
      const headers = this.buildPlainHeaders();

      fetch(this.buildUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true
      }).catch((error) => {
        console.error('Failed to invalidate user session on unload:', error);
      });
    } catch (error) {
      console.error('Failed to invalidate user session on unload:', error);
    }
  }

  private buildUrl(): string {
    const path = this.authContextService.hasInternalAccess
      ? '/internal/user/session/invalidate'
      : '/user/session/invalidate';

    return `${this.apiUrl}${path}`;
  }

  private buildPayload(): SessionInvalidationPayload {
    const idToken = sessionStorage.getItem('id_token') || '';

    return {
      id_token: idToken
    };
  }

  private buildHeaders(): HttpHeaders {
    const token = sessionStorage.getItem('token') || '';
    const idToken = sessionStorage.getItem('id_token') || '';

    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', token);
    }

    if (idToken) {
      headers = headers.set('id_token', idToken);
    }

    return headers;
  }

  private buildPlainHeaders(): Record<string, string> {
    const token = sessionStorage.getItem('token') || '';
    const idToken = sessionStorage.getItem('id_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = token;
    }

    if (idToken) {
      headers['id_token'] = idToken;
    }

    return headers;
  }
}
